import { Trans, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useMemo, useRef } from "react";
import { IoArrowDown } from "react-icons/io5";
import { useKey, useLatest, usePrevious } from "react-use";

import { BASIS_POINTS_DIVISOR, USD_DECIMALS } from "config/factors";
import { useSettings } from "context/SettingsContext/SettingsContextProvider";
import { useTokensData } from "context/SyntheticsStateContext/hooks/globalsHooks";
import { selectChartHeaderInfo } from "context/SyntheticsStateContext/selectors/chartSelectors";
import {
  selectChainId,
  selectMarketsInfoData,
  selectSubaccountState,
} from "context/SyntheticsStateContext/selectors/globalSelectors";
import {
  selectExpressOrdersEnabled,
  selectSettingsWarningDotVisible,
  selectShowDebugValues,
} from "context/SyntheticsStateContext/selectors/settingsSelectors";
import {
  selectTradeboxAllowedSlippage,
  selectTradeboxAvailableTokensOptions,
  selectTradeboxChooseSuitableMarket,
  selectTradeboxDecreasePositionAmounts,
  selectTradeboxExecutionFee,
  selectTradeboxFees,
  selectTradeboxIncreasePositionAmounts,
  selectTradeboxIsWrapOrUnwrap,
  selectTradeboxKeepLeverage,
  selectTradeboxLeverage,
  selectTradeboxLeverageSliderMarks,
  selectTradeboxCurrentPrice,
  selectTradeboxMarkPrice,
  selectTradeboxMaxLeverage,
  selectTradeboxNextPositionValues,
  selectTradeboxSelectedPosition,
  selectTradeboxSelectedPositionKey,
  selectTradeboxSetDefaultAllowedSwapSlippageBps,
  selectTradeboxSetKeepLeverage,
  selectTradeboxSetSelectedAllowedSwapSlippageBps,
  selectTradeboxState,
  selectTradeboxSwapAmounts,
  selectTradeboxTradeFlags,
  selectTradeboxTradeRatios,
} from "context/SyntheticsStateContext/selectors/tradeboxSelectors";
import { useSelector } from "context/SyntheticsStateContext/utils";
import { getMinResidualGasPaymentTokenAmount } from "domain/synthetics/express/expressOrderUtils";
import { MarketInfo, getMarketFullName, getMarketIndexName, getMarketPoolName } from "domain/synthetics/markets";
import { formatLeverage, formatLiquidationPrice } from "domain/synthetics/positions";
import { convertToUsd, getIsEquivalentTokens } from "domain/synthetics/tokens";
import { useMaxAutoCancelOrdersState } from "domain/synthetics/trade/useMaxAutoCancelOrdersState";
import { usePriceImpactWarningState } from "domain/synthetics/trade/usePriceImpactWarningState";
import { MissedCoinsPlace } from "domain/synthetics/userFeedback";
import { Token } from "domain/tokens";
import { useMaxAvailableAmount } from "domain/tokens/useMaxAvailableAmount";
import { useLocalizedMap } from "lib/i18n";
import { throttleLog } from "lib/logging";
import {
  calculateDisplayDecimals,
  formatAmount,
  formatAmountFree,
  formatBalanceAmount,
  formatDeltaUsd,
  formatPercentage,
  formatTokenAmountWithUsd,
  formatUsd,
  formatUsdPrice,
  parseValue,
} from "lib/numbers";
import { EMPTY_ARRAY, getByKey } from "lib/objects";
import { useCursorInside } from "lib/useCursorInside";
import { sendTradeBoxInteractionStartedEvent } from "lib/userAnalytics";
import useWallet from "lib/wallets/useWallet";
import { ASSETCHAIN, ASSETCHAIN_TESTNET } from "sdk/configs/chains";
import { NATIVE_TOKEN_ADDRESS } from "sdk/configs/tokens";
import { TradeMode } from "sdk/types/trade";
import { formatCngnPrice, formatCngnUsd, getCurrencySymbol, isCngnToken } from "sdk/utils/cngnCurrency";

import { AlertInfoCard } from "components/AlertInfo/AlertInfoCard";
import Button from "components/Button/Button";
import BuyInputSection from "components/BuyInputSection/BuyInputSection";
import { LeverageSlider } from "components/LeverageSlider/LeverageSlider";
import { MarketSelector } from "components/MarketSelector/MarketSelector";
import SuggestionInput from "components/SuggestionInput/SuggestionInput";
import { SyntheticsInfoRow } from "components/Synthetics/SyntheticsInfoRow";
import Tabs from "components/Tabs/Tabs";
import ToggleSwitch from "components/ToggleSwitch/ToggleSwitch";
import TokenIcon from "components/TokenIcon/TokenIcon";
import TokenWithIcon from "components/TokenIcon/TokenWithIcon";
import TokenSelector from "components/TokenSelector/TokenSelector";
import Tooltip from "components/Tooltip/Tooltip";
import { ValueTransition } from "components/ValueTransition/ValueTransition";

import SettingsIcon24 from "img/ic_settings_24.svg?react";

import { useIsCurtainOpen } from "./Curtain";
import { ExpressTradingWarningCard } from "./ExpressTradingWarningCard";
import { HighPriceImpactOrFeesWarningCard } from "../HighPriceImpactOrFeesWarningCard/HighPriceImpactOrFeesWarningCard";
import TradeInfoIcon from "../TradeInfoIcon/TradeInfoIcon";
import TwapRows from "../TwapRows/TwapRows";
import { useDecreaseOrdersThatWillBeExecuted } from "./hooks/useDecreaseOrdersThatWillBeExecuted";
import { useExpressTradingWarnings } from "./hooks/useShowOneClickTradingInfo";
import { useTradeboxAcceptablePriceImpactValues } from "./hooks/useTradeboxAcceptablePriceImpactValues";
import { useTradeboxTPSLReset } from "./hooks/useTradeboxTPSLReset";
import { useTradeboxButtonState } from "./hooks/useTradeButtonState";
import { MarketPoolSelectorRow } from "./MarketPoolSelectorRow";
import "./TradeBox.scss";
import { tradeModeLabels, tradeTypeLabels } from "./tradeboxConstants";
import { TradeBoxAdvancedGroups } from "./TradeBoxRows/AdvancedDisplayRows";
import { CollateralSelectorRow } from "./TradeBoxRows/CollateralSelectorRow";
import { LimitAndTPSLGroup } from "./TradeBoxRows/LimitAndTPSLRows";
import { MinReceiveRow } from "./TradeBoxRows/MinReceiveRow";
import { PositionExecutionPreviewRows } from "./TradeBoxRows/PositionExecutionPreviewRows";
import { PriceFeedStatusRow } from "./TradeBoxRows/PriceFeedStatusRow";
import { PriceImpactFeesRow } from "./TradeBoxRows/PriceImpactFeesRow";

import { SwipeTabs } from "components/SwipeTabs/SwipeTabs";

import ArrowSwapIcon from "assets/icons/ArrowSwapIcon";
import SettingIcon from "assets/icons/SettingIcon";

export function TradeBox({ isMobile }: { isMobile: boolean }) {
  useLingui();
  const localizedTradeModeLabels = useLocalizedMap(tradeModeLabels);
  const localizedTradeTypeLabels = useLocalizedMap(tradeTypeLabels);

  const setDefaultAllowedSwapSlippageBps = useSelector(selectTradeboxSetDefaultAllowedSwapSlippageBps);
  const setSelectedAllowedSwapSlippageBps = useSelector(selectTradeboxSetSelectedAllowedSwapSlippageBps);

  const availableTokenOptions = useSelector(selectTradeboxAvailableTokensOptions);
  const chartHeaderInfo = useSelector(selectChartHeaderInfo);
  const formRef = useRef<HTMLFormElement>(null);
  const isCursorInside = useCursorInside(formRef);
  const showDebugValues = useSelector(selectShowDebugValues);

  const allowedSlippage = useSelector(selectTradeboxAllowedSlippage);

  const { swapTokens, infoTokens, sortedLongAndShortTokens, sortedAllMarkets } = availableTokenOptions;
  const tokensData = useTokensData();
  const marketsInfoData = useSelector(selectMarketsInfoData);
  const tradeFlags = useSelector(selectTradeboxTradeFlags);
  const { isLong, isSwap, isIncrease, isPosition, isLimit, isTrigger, isMarket, isTwap } = tradeFlags;
  const isWrapOrUnwrap = useSelector(selectTradeboxIsWrapOrUnwrap);

  const chainId = useSelector(selectChainId);
  const { account } = useWallet();
  const { shouldDisableValidationForTesting: shouldDisableValidation } = useSettings();
  const isDevLiquidityBypassEnabled =
    import.meta.env.VITE_DEV_BYPASS_LIQUIDITY_CHECKS === "true" &&
    (chainId === ASSETCHAIN || chainId === ASSETCHAIN_TESTNET);

  const nativeToken = getByKey(tokensData, NATIVE_TOKEN_ADDRESS);

  const [_, setExternalIsCurtainOpen] = useIsCurtainOpen();

  const {
    fromTokenInputValue,
    setFromTokenInputValue: setFromTokenInputValueRaw,
    toTokenInputValue,
    setToTokenInputValue: setToTokenInputValueRaw,
    setCollateralAddress: onSelectCollateralAddress,
    setFromTokenAddress: onSelectFromTokenAddress,
    setTradeMode: onSelectTradeMode,
    focusedInput,
    setFocusedInput,
    closeSizeInputValue,
    setCloseSizeInputValue,
    triggerPriceInputValue,
    setTriggerPriceInputValue,
    triggerRatioInputValue,
    setTriggerRatioInputValue,
    leverageInputValue,
    setLeverageInputValue,
    leverageOption,
    setLeverageOption,
    isSwitchTokensAllowed,
    switchTokenAddresses,
    tradeMode,
    tradeType,
    collateralToken,
    fromTokenAddress,
    marketInfo,
    toTokenAddress,
    availableTradeModes,
    duration,
    numberOfParts,
    setNumberOfParts,
    setDuration,
    setTradeConfig,
  } = useSelector(selectTradeboxState);

  const fromToken = getByKey(tokensData, fromTokenAddress);
  const toToken = getByKey(tokensData, toTokenAddress);
  const fromTokenAmount = fromToken ? parseValue(fromTokenInputValue || "0", fromToken.decimals)! : 0n;
  const fromTokenPrice = fromToken?.prices.minPrice;
  const fromUsd = convertToUsd(fromTokenAmount, fromToken?.decimals, fromTokenPrice);

  const closeSizeUsd = parseValue(closeSizeInputValue || "0", USD_DECIMALS)!;

  const currentPrice = useSelector(selectTradeboxCurrentPrice);
  const markPrice = useSelector(selectTradeboxMarkPrice);
  const swapAmounts = useSelector(selectTradeboxSwapAmounts);
  const increaseAmounts = useSelector(selectTradeboxIncreasePositionAmounts);
  const decreaseAmounts = useSelector(selectTradeboxDecreasePositionAmounts);
  const selectedPositionKey = useSelector(selectTradeboxSelectedPositionKey);
  const selectedPosition = useSelector(selectTradeboxSelectedPosition);
  const leverage = useSelector(selectTradeboxLeverage);
  const nextPositionValues = useSelector(selectTradeboxNextPositionValues);
  const fees = useSelector(selectTradeboxFees);
  const expressOrdersEnabled = useSelector(selectExpressOrdersEnabled);
  const { subaccount } = useSelector(selectSubaccountState);

  const executionFee = useSelector(selectTradeboxExecutionFee);
  const { markRatio } = useSelector(selectTradeboxTradeRatios);

  const leverageSliderMarks = useSelector(selectTradeboxLeverageSliderMarks);

  const maxLeverage = useSelector(selectTradeboxMaxLeverage);

  const selectedMarketDisplayName = marketInfo
    ? getMarketFullName(marketInfo)
    : toToken
      ? getMarketIndexName({ indexToken: toToken, isSpotOnly: false })
      : undefined;
  const selectedMarketPoolName = marketInfo
    ? getMarketPoolName({ longToken: marketInfo.longToken, shortToken: marketInfo.shortToken }, " / ")
    : undefined;

  const maxAllowedLeverage = maxLeverage / 2;

  const decreaseOrdersThatWillBeExecuted = useDecreaseOrdersThatWillBeExecuted();

  const priceImpactWarningState = usePriceImpactWarningState({
    collateralImpact: fees?.positionCollateralPriceImpact,
    positionImpact: fees?.positionPriceImpact,
    swapPriceImpact: fees?.swapPriceImpact,
    swapProfitFee: fees?.swapProfitFee,
    executionFeeUsd: executionFee?.feeUsd,
    willDecreaseOrdersBeExecuted: decreaseOrdersThatWillBeExecuted.length > 0,
    externalSwapFeeItem: fees?.externalSwapFee,
    tradeFlags,
    payUsd: fromUsd,
  });

  const setIsDismissedRef = useLatest(priceImpactWarningState.setIsDismissed);

  const setFromTokenInputValue = useCallback(
    (value: string, resetPriceImpactAndSwapSlippage?: boolean) => {
      setFromTokenInputValueRaw(value);

      if (resetPriceImpactAndSwapSlippage) {
        setIsDismissedRef.current(false);

        setDefaultAllowedSwapSlippageBps(undefined);
        setSelectedAllowedSwapSlippageBps(undefined);
      }
    },
    [setFromTokenInputValueRaw, setIsDismissedRef, setDefaultAllowedSwapSlippageBps, setSelectedAllowedSwapSlippageBps]
  );

  const setToTokenInputValue = useCallback(
    (value: string, shouldResetPriceImpactAndSwapSlippage: boolean) => {
      setToTokenInputValueRaw(value);
      if (shouldResetPriceImpactAndSwapSlippage) {
        setIsDismissedRef.current(false);

        setDefaultAllowedSwapSlippageBps(undefined);
        setSelectedAllowedSwapSlippageBps(undefined);
      }
    },
    [setIsDismissedRef, setToTokenInputValueRaw, setDefaultAllowedSwapSlippageBps, setSelectedAllowedSwapSlippageBps]
  );

  const { warning: maxAutoCancelOrdersWarning } = useMaxAutoCancelOrdersState({
    positionKey: selectedPositionKey,
    isCreatingNewAutoCancel: isTrigger,
  });

  const submitButtonState = useTradeboxButtonState({
    account,
    setToTokenInputValue,
    priceImpactWarningState,
  });

  const wrappedOnSubmit = useCallback(async () => {
    await submitButtonState.onSubmit();
    if (isMobile) {
      setExternalIsCurtainOpen(false);
    }
  }, [submitButtonState, isMobile, setExternalIsCurtainOpen]);

  const { formattedMaxAvailableAmount, maxAvailableAmount, showClickMax } = useMaxAvailableAmount({
    fromToken,
    nativeToken,
    fromTokenAmount,
    fromTokenInputValue,
    minResidualAmount: getMinResidualGasPaymentTokenAmount({
      expressParams: submitButtonState.expressParams,
      payTokenAddress: fromTokenAddress,
    }),
    isLoading: submitButtonState.isExpressLoading,
  });

  const onMaxClick = useCallback(() => {
    if (formattedMaxAvailableAmount) {
      setFocusedInput("from");
      setFromTokenInputValue(formattedMaxAvailableAmount, true);
    }
  }, [formattedMaxAvailableAmount, setFocusedInput, setFromTokenInputValue]);

  const handlePayPercentageChange = useCallback(
    (percentage: number) => {
      if (!fromToken || maxAvailableAmount <= 0n) return;

      setFocusedInput("from");
      setFromTokenInputValue(
        formatAmountFree((maxAvailableAmount * BigInt(percentage)) / 100n, fromToken.decimals, fromToken.decimals),
        true
      );
    },
    [fromToken, maxAvailableAmount, setFocusedInput, setFromTokenInputValue]
  );

  useTradeboxAcceptablePriceImpactValues();
  useTradeboxTPSLReset(priceImpactWarningState.setIsDismissed);

  const prevIsISwap = usePrevious(isSwap);

  useEffect(
    function updateInputAmounts() {
      if (!fromToken || !toToken || (!isSwap && !isIncrease)) {
        return;
      }

      // reset input values when switching between swap and position tabs
      if (prevIsISwap !== undefined && isSwap !== prevIsISwap) {
        setFocusedInput("from");

        setFromTokenInputValue("", true);
        return;
      }

      if (isSwap && swapAmounts) {
        if (focusedInput === "from") {
          setToTokenInputValue(
            swapAmounts.amountOut > 0 ? formatAmountFree(swapAmounts.amountOut, toToken.decimals) : "",
            false
          );
        } else {
          setFromTokenInputValue(
            swapAmounts.amountIn > 0 ? formatAmountFree(swapAmounts.amountIn, fromToken.decimals) : "",
            false
          );
        }
      }

      if (isIncrease && increaseAmounts) {
        const visualMultiplier = BigInt(toToken.visualMultiplier ?? 1);
        if (focusedInput === "from") {
          setToTokenInputValue(
            increaseAmounts.indexTokenAmount > 0
              ? formatAmountFree(increaseAmounts.indexTokenAmount / visualMultiplier, toToken.decimals)
              : "",
            false
          );
        } else {
          setFromTokenInputValue(
            increaseAmounts.initialCollateralAmount > 0
              ? formatAmountFree(increaseAmounts.initialCollateralAmount, fromToken.decimals)
              : "",
            false
          );
        }
      }
    },
    [
      focusedInput,
      fromToken,
      increaseAmounts,
      isIncrease,
      isSwap,
      prevIsISwap,
      setFocusedInput,
      setFromTokenInputValue,
      setToTokenInputValue,
      swapAmounts,
      toToken,
    ]
  );

  useEffect(
    function resetTriggerPrice() {
      setTriggerPriceInputValue("");
    },
    [setTriggerPriceInputValue, toTokenAddress, tradeMode]
  );

  useEffect(
    function validateLeverageOption() {
      if (leverageOption && leverageOption > maxAllowedLeverage / BASIS_POINTS_DIVISOR) {
        setLeverageOption(maxAllowedLeverage / BASIS_POINTS_DIVISOR);
      }
    },
    [leverageOption, maxAllowedLeverage, setLeverageOption]
  );

  useEffect(
    function tradeBoxInteractionStartedEffect() {
      if (fromTokenInputValue.length > 0) {
        let pair = "";

        if (isSwap) {
          pair = `${fromToken?.symbol}/${toToken?.symbol}`;
        } else if (marketInfo) {
          pair = marketInfo.name;
        }

        let sizeDeltaUsd: bigint | undefined = undefined;
        let amountUsd: bigint | undefined = undefined;
        let priceImpactDeltaUsd = 0n;
        let priceImpactPercentage = 0n;

        if (isIncrease && increaseAmounts) {
          sizeDeltaUsd = increaseAmounts.sizeDeltaUsd;
          priceImpactDeltaUsd = increaseAmounts.positionPriceImpactDeltaUsd;
          priceImpactPercentage = fees?.positionPriceImpact?.precisePercentage ?? 0n;
        } else if (isSwap && swapAmounts) {
          amountUsd = swapAmounts.usdOut;
          priceImpactDeltaUsd = swapAmounts.swapStrategy.swapPathStats?.totalSwapPriceImpactDeltaUsd ?? 0n;
          priceImpactPercentage = fees?.swapPriceImpact?.precisePercentage ?? 0n;
        } else if (isTrigger && decreaseAmounts) {
          sizeDeltaUsd = decreaseAmounts.sizeDeltaUsd;
          priceImpactDeltaUsd = decreaseAmounts.positionPriceImpactDeltaUsd;
          priceImpactPercentage = fees?.positionPriceImpact?.precisePercentage ?? 0n;
        }

        const openInterestPercent = isLong
          ? chartHeaderInfo?.longOpenInterestPercentage
          : chartHeaderInfo?.shortOpenInterestPercentage;
        const fundingRate1h = isLong ? chartHeaderInfo?.fundingRateLong : chartHeaderInfo?.fundingRateShort;

        if (!pair) {
          return;
        }

        sendTradeBoxInteractionStartedEvent({
          pair,
          sizeDeltaUsd,
          priceImpactDeltaUsd,
          priceImpactPercentage,
          fundingRate1h,
          isExpress: expressOrdersEnabled,
          isExpress1CT: Boolean(subaccount),
          openInterestPercent,
          tradeType,
          tradeMode,
          amountUsd,
        });
      }
    },
    [
      chainId,
      chartHeaderInfo,
      decreaseAmounts,
      expressOrdersEnabled,
      fees,
      fromToken?.symbol,
      fromTokenInputValue,
      increaseAmounts,
      isIncrease,
      isLong,
      isSwap,
      isTrigger,
      marketInfo,
      subaccount,
      swapAmounts,
      toToken?.symbol,
      tradeMode,
      tradeType,
    ]
  );

  const onSwitchTokens = useCallback(() => {
    // Always use forward ("from") calculation after switching to avoid
    // reverse estimation issues on chains with perp-market price impact
    setFocusedInput("from");
    switchTokenAddresses();
    setFromTokenInputValue(toTokenInputValue || "", true);
    setToTokenInputValue("", false);
  }, [setFocusedInput, setFromTokenInputValue, setToTokenInputValue, switchTokenAddresses, toTokenInputValue]);

  const onSelectToTokenAddress = useSelector(selectTradeboxChooseSuitableMarket);

  if (showDebugValues) {
    const swapPathStats = swapAmounts?.swapStrategy.swapPathStats || increaseAmounts?.swapStrategy.swapPathStats;

    if (swapPathStats) {
      // eslint-disable-next-line no-console
      throttleLog("Swap Path", {
        steps: swapPathStats.swapSteps,
        path: swapPathStats.swapPath.map((marketAddress) => marketsInfoData?.[marketAddress]?.name).join(" -> "),
        priceImpact: swapPathStats.swapSteps.map((step) => formatDeltaUsd(step.priceImpactDeltaUsd)).join(" -> "),
        usdOut: swapPathStats.swapSteps.map((step) => formatUsd(step.usdOut)).join(" -> "),
      });
    }
  }

  const handleFromInputTokenChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setFocusedInput("from");
      setFromTokenInputValue(event.target.value, true);
    },
    [setFocusedInput, setFromTokenInputValue]
  );
  const handleToInputTokenChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setFocusedInput("to");
      setToTokenInputValue(event.target.value, true);
    },
    [setFocusedInput, setToTokenInputValue]
  );
  const handleSelectFromTokenAddress = useCallback(
    (token: Token) => onSelectFromTokenAddress(token.address),
    [onSelectFromTokenAddress]
  );
  const handleSelectToTokenAddress = useCallback(
    (token: Token) => onSelectToTokenAddress(token.address),
    [onSelectToTokenAddress]
  );
  const getPayTokenState = useCallback(
    (tokenOptionInfo) => {
      if (!isIncrease || !marketInfo) {
        return;
      }

      const isTokenInSelectedPool =
        getIsEquivalentTokens(tokenOptionInfo, marketInfo.longToken) ||
        getIsEquivalentTokens(tokenOptionInfo, marketInfo.shortToken);

      if (isTokenInSelectedPool) {
        return;
      }

      return {
        disabled: true,
        message: t`Token is not available in selected pool.`,
      };
    },
    [isIncrease, marketInfo]
  );
  const handleCloseInputChange = useCallback((e) => setCloseSizeInputValue(e.target.value), [setCloseSizeInputValue]);

  const formattedMaxCloseSize = formatAmount(selectedPosition?.sizeInUsd, USD_DECIMALS, 2);

  const setMaxCloseSize = useCallback(
    () => setCloseSizeInputValue(formattedMaxCloseSize),
    [formattedMaxCloseSize, setCloseSizeInputValue]
  );
  const handleClosePercentageChange = useCallback(
    (percent: number) =>
      setCloseSizeInputValue(
        formatAmount(((selectedPosition?.sizeInUsd ?? 0n) * BigInt(percent)) / 100n, USD_DECIMALS, 2)
      ),
    [selectedPosition?.sizeInUsd, setCloseSizeInputValue]
  );

  const handleTriggerPriceInputChange = useCallback(
    (e) => setTriggerPriceInputValue(e.target.value),
    [setTriggerPriceInputValue]
  );

  const setMarkPriceAsTriggerPrice = useCallback(() => {
    if (markPrice === undefined) {
      return;
    }

    setTriggerPriceInputValue(
      formatAmount(
        markPrice,
        USD_DECIMALS,
        calculateDisplayDecimals(markPrice, undefined, toToken?.visualMultiplier),
        undefined,
        undefined,
        toToken?.visualMultiplier
      )
    );
  }, [markPrice, setTriggerPriceInputValue, toToken?.visualMultiplier]);

  const handleTriggerMarkPriceClick = useCallback(
    () => setTriggerRatioInputValue(formatAmount(markRatio?.ratio, USD_DECIMALS, 10)),
    [markRatio?.ratio, setTriggerRatioInputValue]
  );
  const handleTriggerRatioInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setTriggerRatioInputValue(e.target.value);
    },
    [setTriggerRatioInputValue]
  );

  const handleSelectMarket = useCallback(
    (_indexName: string, marketInfo: MarketInfo) => {
      const longTokenAddress = marketInfo.longTokenAddress;
      const shortTokenAddress = marketInfo.shortTokenAddress;
      const isLongTokenStable = marketInfo.longToken.isStable;
      const isShortTokenStable = marketInfo.shortToken.isStable;

      let preferredCollateralAddress: string;

      if (longTokenAddress === shortTokenAddress) {
        preferredCollateralAddress = shortTokenAddress;
      } else if (isLongTokenStable && !isShortTokenStable) {
        preferredCollateralAddress = longTokenAddress;
      } else if (!isLongTokenStable && isShortTokenStable) {
        preferredCollateralAddress = shortTokenAddress;
      } else if (isLong) {
        preferredCollateralAddress = longTokenAddress;
      } else {
        preferredCollateralAddress = shortTokenAddress;
      }

      setTradeConfig({
        tradeType,
        toTokenAddress: marketInfo.indexToken.address,
        marketAddress: marketInfo.marketTokenAddress,
        collateralAddress: preferredCollateralAddress,
        ...(isIncrease ? { fromTokenAddress: preferredCollateralAddress } : {}),
      });
    },
    [isIncrease, isLong, setTradeConfig, tradeType]
  );

  const handleFormSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (!isCursorInside && (!submitButtonState.disabled || shouldDisableValidation)) {
        wrappedOnSubmit();
      }
    },
    [isCursorInside, wrappedOnSubmit, submitButtonState, shouldDisableValidation]
  );

  const handleLeverageInputBlur = useCallback(() => {
    if (leverageOption === 0) {
      setLeverageOption(leverageSliderMarks[0]);
      return;
    }

    if (leverageInputValue === "" && leverageOption !== undefined) {
      setLeverageInputValue(leverageOption.toString());
    }
  }, [leverageInputValue, leverageOption, leverageSliderMarks, setLeverageInputValue, setLeverageOption]);

  const handleLeverageInputKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        e.preventDefault();

        const isAlt = e.altKey;
        const direction = e.key === "ArrowUp" ? 1 : -1;
        const increment = isAlt ? 0.1 : 1;
        const diff = direction * increment;
        const newValue = Math.round(((leverageOption ?? leverageSliderMarks[0]) + diff) * 10) / 10;
        const clampedValue = Math.min(Math.max(newValue, leverageSliderMarks[0]), leverageSliderMarks.at(-1)!);

        setLeverageOption(clampedValue);
      }
    },
    [leverageOption, leverageSliderMarks, setLeverageOption]
  );

  const payUsd = isIncrease ? increaseAmounts?.initialCollateralUsd : fromUsd;

  // CNGN price in USD — used for ₦ conversion in display
  const cngnPrice = isCngnToken(toToken?.symbol) ? toToken?.prices?.minPrice : undefined;

  function renderTokenInputs() {
    return (
      <>
        <BuyInputSection
          leftColumnMinWidth={126}
          topLeftLabel={t`Pay`}
          bottomLeftValue={
            payUsd !== undefined ? formatCngnUsd(payUsd, toToken?.symbol, cngnPrice) ?? formatUsd(payUsd) : ""
          }
          isBottomLeftValueMuted={payUsd === undefined || payUsd === 0n}
          bottomRightLabel={t`Balance`}
          bottomRightValue={
            fromToken && fromToken.balance !== undefined && fromToken.balance > 0n
              ? formatBalanceAmount(fromToken.balance, fromToken.decimals, fromToken.symbol, {
                  isStable: fromToken.isStable,
                })
              : undefined
          }
          inputValue={fromTokenInputValue}
          onInputValueChange={handleFromInputTokenChange}
          onClickMax={showClickMax ? onMaxClick : undefined}
          showPercentSelector={maxAvailableAmount > 0n}
          onPercentChange={handlePayPercentageChange}
          qa="pay"
        >
          {fromTokenAddress && (
            <TokenSelector
              label={t`Pay`}
              chainId={chainId}
              tokenAddress={fromTokenAddress}
              onSelectToken={handleSelectFromTokenAddress}
              tokens={swapTokens}
              infoTokens={infoTokens}
              size="l"
              showSymbolImage={true}
              showTokenImgInDropdown={true}
              missedCoinsPlace={MissedCoinsPlace.payToken}
              extendedSortSequence={sortedLongAndShortTokens}
              getTokenState={getPayTokenState}
              qa="collateral-selector"
              variant="outlined"
            />
          )}
        </BuyInputSection>

        {isSwap && (
          <>
            <div className="relative">
              {!isTwap && (
                <button
                  type="button"
                  disabled={!isSwitchTokensAllowed}
                  className="absolute -top-19 left-1/2 flex h-32 w-40 -translate-x-1/2 cursor-pointer items-center justify-center rounded-28 border border-solid border-gray-light bg-gray-dark text-white-alpha-60 active:border-white-alpha-8 active:bg-white-alpha-2 active:text-white
                           desktop-hover:bg-white-alpha-8 desktop-hover:text-white-alpha-80"
                  onClick={onSwitchTokens}
                  data-qa="swap-ball"
                >
                  <ArrowSwapIcon className="h-[20px] w-[20px]" />
                </button>
              )}
              <BuyInputSection
                leftColumnMinWidth={126}
                topLeftLabel={isTwap ? t`Receive (Approximate)` : t`Receive`}
                bottomLeftValue={
                  !isTwap && swapAmounts?.usdOut !== undefined
                    ? formatCngnUsd(swapAmounts?.usdOut, toToken?.symbol, cngnPrice) ?? formatUsd(swapAmounts?.usdOut)
                    : undefined
                }
                bottomRightValue={
                  !isTwap && toToken && toToken.balance !== undefined && toToken.balance > 0n
                    ? formatBalanceAmount(toToken.balance, toToken.decimals, toToken.symbol, {
                        isStable: toToken.isStable,
                      })
                    : undefined
                }
                isBottomLeftValueMuted={swapAmounts?.usdOut === undefined || swapAmounts.usdOut === 0n}
                inputValue={toTokenInputValue}
                onInputValueChange={handleToInputTokenChange}
                qa="swap-receive"
                isDisabled={isTwap}
              >
                {toTokenAddress && (
                  <TokenSelector
                    label={t`Receive`}
                    chainId={chainId}
                    tokenAddress={toTokenAddress}
                    onSelectToken={handleSelectToTokenAddress}
                    tokens={swapTokens}
                    infoTokens={infoTokens}
                    showSymbolImage={true}
                    showBalances={true}
                    showTokenImgInDropdown={true}
                    extendedSortSequence={sortedLongAndShortTokens}
                    qa="receive-selector"
                    variant="outlined"
                  />
                )}
              </BuyInputSection>
            </div>
          </>
        )}

        {isIncrease && (
          <BuyInputSection
            leftColumnMinWidth={126}
            topLeftLabel={localizedTradeTypeLabels[tradeType!]}
            bottomLeftValue={
              increaseAmounts?.sizeDeltaUsd !== undefined
                ? formatCngnUsd(increaseAmounts?.sizeDeltaUsd, toToken?.symbol, cngnPrice, { fallbackToZero: true }) ??
                  formatUsd(increaseAmounts?.sizeDeltaUsd, { fallbackToZero: true })
                : ""
            }
            isBottomLeftValueMuted={increaseAmounts?.sizeDeltaUsd === undefined || increaseAmounts?.sizeDeltaUsd === 0n}
            bottomRightLabel={t`Leverage`}
            bottomRightValue={
              formatLeverage(isLeverageSliderEnabled ? leverage : increaseAmounts?.estimatedLeverage) || "-"
            }
            inputValue={toTokenInputValue}
            onInputValueChange={handleToInputTokenChange}
            qa="buy"
          >
            {toTokenAddress && (
              <MarketSelector
                chainId={chainId}
                label={localizedTradeTypeLabels[tradeType!]}
                selectedIndexName={selectedMarketDisplayName}
                selectedMarketLabel={
                  toToken && (
                    <span className="inline-flex min-w-0 items-start gap-6">
                      <TokenIcon symbol={toToken.symbol} importSize={24} displaySize={20} />
                      <span className="MarketSelector-label MarketSelector-label--compact MarketSelector-label--tradebox">
                        <span className="MarketSelector-label-main">{selectedMarketPoolName ?? "Select Pool"}</span>
                      </span>
                    </span>
                  )
                }
                markets={sortedAllMarkets ?? EMPTY_ARRAY}
                isSideMenu
                missedCoinsPlace={MissedCoinsPlace.marketDropdown}
                onSelectMarket={handleSelectMarket}
                size="l"
              />
            )}
          </BuyInputSection>
        )}
      </>
    );
  }

  function renderDecreaseSizeInput() {
    const showMaxButton = Boolean(
      selectedPosition?.sizeInUsd && selectedPosition.sizeInUsd > 0 && closeSizeInputValue !== formattedMaxCloseSize
    );

    return (
      <BuyInputSection
        topLeftLabel={t`Close`}
        bottomRightValue={
          selectedPosition?.sizeInUsd
            ? formatCngnUsd(selectedPosition.sizeInUsd, toToken?.symbol, cngnPrice) ??
              formatUsd(selectedPosition.sizeInUsd)
            : undefined
        }
        isBottomLeftValueMuted={closeSizeUsd === 0n}
        bottomLeftValue={formatCngnUsd(closeSizeUsd, toToken?.symbol, cngnPrice) ?? formatUsd(closeSizeUsd)}
        inputValue={closeSizeInputValue}
        onInputValueChange={handleCloseInputChange}
        onClickBottomRightLabel={setMaxCloseSize}
        onClickMax={showMaxButton ? setMaxCloseSize : undefined}
        showPercentSelector={selectedPosition?.sizeInUsd ? selectedPosition.sizeInUsd > 0 : false}
        onPercentChange={handleClosePercentageChange}
        qa="close"
      >
        {getCurrencySymbol(toToken?.symbol) === "₦" ? "NGN" : "USD"}
      </BuyInputSection>
    );
  }

  function renderTriggerPriceInput() {
    const priceLabel = isLimit ? (tradeMode === TradeMode.Limit ? t`Limit Price` : t`Stop Price`) : t`Trigger Price`;

    return (
      <BuyInputSection
        topLeftLabel={priceLabel}
        topRightContent={
          <div className="flex flex-col items-end gap-2 text-[11px] leading-[14px]">
            <div className="flex items-baseline gap-4">
              <span className="text-gray-lighter">{t`Current`}:</span>
              <span>
                {formatCngnPrice(currentPrice, toToken?.symbol, {
                  visualMultiplier: toToken?.visualMultiplier,
                })}
              </span>
            </div>
            <div className="flex items-baseline gap-4">
              <span className="text-gray-lighter">{t`Mark Price`}:</span>
              <span>
                {formatCngnPrice(markPrice, toToken?.symbol, {
                  visualMultiplier: toToken?.visualMultiplier,
                })}
              </span>
            </div>
          </div>
        }
        onClickTopRightLabel={setMarkPriceAsTriggerPrice}
        inputValue={triggerPriceInputValue}
        onInputValueChange={handleTriggerPriceInputChange}
        qa="trigger-price"
      >
        {getCurrencySymbol(toToken?.symbol) === "₦" ? "NGN" : "USD"}
      </BuyInputSection>
    );
  }

  function renderTriggerRatioInput() {
    return (
      <BuyInputSection
        topLeftLabel={t`Limit Price`}
        topRightLabel={t`Mark`}
        topRightValue={formatAmount(markRatio?.ratio, USD_DECIMALS, 4)}
        onClickTopRightLabel={handleTriggerMarkPriceClick}
        inputValue={triggerRatioInputValue}
        onInputValueChange={handleTriggerRatioInputChange}
        qa="trigger-price"
      >
        {markRatio && (
          <>
            <TokenWithIcon symbol={markRatio.smallestToken.symbol} displaySize={16} className="text-[14px]" />{" "}
            <span className="text-[14px]"><Trans>per</Trans></span>{" "}
            <TokenWithIcon symbol={markRatio.largestToken.symbol} displaySize={16} className="text-[14px]" />
          </>
        )}
      </BuyInputSection>
    );
  }

  useKey(
    "Enter",
    () => {
      if (isCursorInside && (!submitButtonState.disabled || shouldDisableValidation)) {
        wrappedOnSubmit();
      }
    },
    {},
    [submitButtonState.disabled, shouldDisableValidation, isCursorInside, wrappedOnSubmit]
  );

  const buttonContent = (
    <Button
      qa="confirm-trade-button"
      variant="primary"
      className="w-full !py-12 [text-decoration:inherit]"
      onClick={wrappedOnSubmit}
      disabled={submitButtonState.disabled && !shouldDisableValidation}
    >
      {submitButtonState.text}
    </Button>
  );
  const button = submitButtonState.tooltipContent ? (
    <Tooltip
      className="w-full"
      content={submitButtonState.tooltipContent}
      handle={buttonContent}
      isHandlerDisabled
      handleClassName="w-full"
      position="bottom"
    />
  ) : (
    buttonContent
  );

  let nextLiqPriceFormatted = useMemo(() => {
    if (isTrigger && decreaseAmounts?.isFullClose) {
      return "-";
    }

    if (isIncrease && (increaseAmounts === undefined || increaseAmounts.sizeDeltaUsd === 0n)) {
      if (selectedPosition) {
        return undefined;
      } else {
        return "-";
      }
    }

    return formatLiquidationPrice(nextPositionValues?.nextLiqPrice, {
      visualMultiplier: toToken?.visualMultiplier,
    });
  }, [
    isTrigger,
    decreaseAmounts?.isFullClose,
    isIncrease,
    increaseAmounts,
    nextPositionValues?.nextLiqPrice,
    toToken?.visualMultiplier,
    selectedPosition,
  ]);

  const keepLeverage = useSelector(selectTradeboxKeepLeverage);
  const keepLeverageChecked = decreaseAmounts?.isFullClose ? false : keepLeverage ?? false;
  const setKeepLeverage = useSelector(selectTradeboxSetKeepLeverage);
  const settingsWarningDotVisible = useSelector(selectSettingsWarningDotVisible);

  const { setIsSettingsVisible, isLeverageSliderEnabled } = useSettings();

  const { shouldShowWarning: shouldShowOneClickTradingWarning } = useExpressTradingWarnings({
    expressParams: submitButtonState.expressParams,
    payTokenAddress: fromTokenAddress,
    isWrapOrUnwrap,
  });

  const showSectionBetweenInputsAndButton =
    isPosition ||
    (priceImpactWarningState.shouldShowWarning && !priceImpactWarningState.isDismissed) ||
    (!isTrigger && !isSwap) ||
    (isSwap && isLimit) ||
    (isSwap && isTwap) ||
    maxAutoCancelOrdersWarning ||
    shouldShowOneClickTradingWarning;

  const tabsOptions = useMemo(() => {
    const modeToOptions = (mode: TradeMode) => ({
      value: mode,
      label: localizedTradeModeLabels[mode],
    });

    return availableTradeModes.map((mode) =>
      Array.isArray(mode)
        ? {
            label: t`More`,
            options: mode.map(modeToOptions),
          }
        : modeToOptions(mode)
    );
  }, [availableTradeModes, localizedTradeModeLabels]);

  return (
    <>
      <div className="mt-[12px] flex items-center justify-between">
        <Tabs
          options={tabsOptions}
          type="inline"
          selectedValue={tradeMode}
          onChange={onSelectTradeMode}
          qa="trade-mode"
          variant="outlined"
          size="small"
        />
        {/* <SwipeTabs options={tabsOptions} /> */}
        <div className="flex gap-4">
          <TradeInfoIcon isMobile={isMobile} tradeType={tradeType} tradePlace="tradebox" />
          {/* <div className="relative">
            <SettingIcon
              className="cursor-pointer text-white-alpha-60 Nectar-hover:text-white"
              onClick={() => setIsSettingsVisible(true)}
            />
            {settingsWarningDotVisible && <div className="absolute bottom-6 right-3 h-6 w-6 rounded-full bg-red-400" />}
          </div> */}
        </div>
      </div>
      <form onSubmit={handleFormSubmit} ref={formRef} className="text-body-medium mt-[8px] flex grow flex-col">
        <div className="flex flex-col gap-6">
          {(isSwap || isIncrease) && renderTokenInputs()}
          {isTrigger && renderDecreaseSizeInput()}
          {isSwap && isLimit && renderTriggerRatioInput()}
          {isPosition && (isLimit || isTrigger) && renderTriggerPriceInput()}
        </div>

        {showSectionBetweenInputsAndButton && (
          <div className="flex flex-col gap-9 pt-16">
            {maxAutoCancelOrdersWarning}
            {isSwap && isLimit && !isTwap && (
              <AlertInfoCard key="showHasBetterOpenFeesAndNetFeesWarning">
                <Trans>
                  The actual trigger price at which order gets filled will depend on fees and price impact at the time
                  of execution to guarantee that you receive the minimum receive amount.
                </Trans>
              </AlertInfoCard>
            )}

            {isPosition && (
              <>
                <PriceFeedStatusRow />

                {isIncrease && isLeverageSliderEnabled && (
                  <div className="relative mb-15 flex items-center gap-6">
                    <LeverageSlider
                      className="relative z-10 grow"
                      marks={leverageSliderMarks}
                      value={leverageOption}
                      onChange={setLeverageOption}
                      isPositive={isLong}
                      slimLevel={2}
                    />
                    <div className="absolute right-65 top-1/2 z-0 h-2 w-14 -translate-y-1/2 bg-[#454545]" />
                    <SuggestionInput
                      className="w-65"
                      inputClassName="text-clip"
                      value={leverageInputValue}
                      setValue={setLeverageInputValue}
                      onBlur={handleLeverageInputBlur}
                      onKeyDown={handleLeverageInputKeyDown}
                      symbol="x"
                    />
                  </div>
                )}
                {isTrigger && (
                  <SyntheticsInfoRow
                    label={t`Market`}
                    value={
                      <MarketSelector
                        chainId={chainId}
                        label={t`Market`}
                        className="-mr-4"
                        selectedIndexName={selectedMarketDisplayName}
                        markets={sortedAllMarkets ?? EMPTY_ARRAY}
                        isSideMenu
                        onSelectMarket={handleSelectMarket}
                        size="m"
                      />
                    }
                  />
                )}

                <MarketPoolSelectorRow />

                <CollateralSelectorRow
                  selectedMarketAddress={marketInfo?.marketTokenAddress}
                  onSelectCollateralAddress={onSelectCollateralAddress}
                  isMarket={isMarket}
                />

                <SyntheticsInfoRow
                  label={t`Margin Mode`}
                  value={
                    <Tooltip
                      position="left-start"
                      handle={<span className="cursor-help text-white"><Trans>Isolated</Trans></span>}
                      content={
                        <Trans>
                          Positions are isolated by market, collateral token, and side. Cross margin is not available
                          for this market type.
                        </Trans>
                      }
                    />
                  }
                />

                <PositionExecutionPreviewRows />

                {isTrigger && selectedPosition && selectedPosition?.leverage !== undefined && (
                  <ToggleSwitch
                    isChecked={keepLeverageChecked}
                    setIsChecked={setKeepLeverage}
                    disabled={decreaseAmounts?.isFullClose}
                  >
                    <span className="text-14 text-gray-lighter">
                      <Trans>Keep leverage at {formatLeverage(selectedPosition.leverage)}</Trans>
                    </span>
                  </ToggleSwitch>
                )}
              </>
            )}

            {isTwap && (
              <TwapRows
                duration={duration}
                numberOfParts={numberOfParts}
                setNumberOfParts={setNumberOfParts}
                setDuration={setDuration}
                sizeUsd={isSwap ? payUsd : increaseAmounts?.sizeDeltaUsd}
                marketInfo={marketInfo}
                type={isSwap ? "swap" : "increase"}
                isLong={isLong}
              />
            )}

            {!isTrigger && !isSwap && !isTwap && <LimitAndTPSLGroup />}
            {priceImpactWarningState.shouldShowWarning && !priceImpactWarningState.isDismissed && (
              <HighPriceImpactOrFeesWarningCard
                priceImpactWarningState={priceImpactWarningState}
                collateralImpact={fees?.positionCollateralPriceImpact}
                positionImpact={fees?.positionPriceImpact}
                swapPriceImpact={fees?.swapPriceImpact}
                swapProfitFee={fees?.swapProfitFee}
                executionFeeUsd={executionFee?.feeUsd}
                externalSwapFeeItem={fees?.externalSwapFee}
              />
            )}
          </div>
        )}
        <div className="flex flex-col gap-14 pt-14">
          <div>{button}</div>
          {isDevLiquidityBypassEnabled && (
            <div className="text-yellow-400 text-12">
              <Trans>Liquidity checks are bypassed (dev only). Use with caution.</Trans>
            </div>
          )}
          <ExpressTradingWarningCard
            expressParams={submitButtonState.expressParams}
            payTokenAddress={!tradeFlags.isTrigger ? fromTokenAddress : undefined}
            isWrapOrUnwrap={!tradeFlags.isTrigger && isWrapOrUnwrap}
          />
          <div className="h-1 bg-stroke-primary" />
          {isSwap && !isTwap && <MinReceiveRow allowedSlippage={allowedSlippage} />}
          {isTrigger && selectedPosition && decreaseAmounts?.receiveUsd !== undefined && (
            <SyntheticsInfoRow
              label={t`Receive`}
              value={formatTokenAmountWithUsd(
                decreaseAmounts.receiveTokenAmount,
                decreaseAmounts.receiveUsd,
                collateralToken?.symbol,
                collateralToken?.decimals,
                { isStable: collateralToken?.isStable }
              )}
            />
          )}

          {isTrigger && (
            <SyntheticsInfoRow
              label={t`PnL`}
              value={
                <ValueTransition
                  from={
                    <>
                      {formatDeltaUsd(decreaseAmounts?.estimatedPnl)} (
                      {formatPercentage(decreaseAmounts?.estimatedPnlPercentage, { signed: true })})
                    </>
                  }
                  to={
                    decreaseAmounts?.sizeDeltaUsd && decreaseAmounts.sizeDeltaUsd > 0 ? (
                      <>
                        {formatDeltaUsd(nextPositionValues?.nextPnl)} (
                        {formatPercentage(nextPositionValues?.nextPnlPercentage, { signed: true })})
                      </>
                    ) : undefined
                  }
                />
              }
            />
          )}
          {!(isTrigger && !selectedPosition) && !isSwap && !isTwap && (
            <SyntheticsInfoRow
              label={t`Liquidation Price`}
              value={
                <ValueTransition
                  from={
                    selectedPosition
                      ? formatLiquidationPrice(selectedPosition?.liquidationPrice, {
                          visualMultiplier: toToken?.visualMultiplier,
                        })
                      : undefined
                  }
                  to={nextLiqPriceFormatted}
                />
              }
            />
          )}
          {!isTwap && <PriceImpactFeesRow />}
          <TradeBoxAdvancedGroups
            slippageInputId={submitButtonState.slippageInputId}
            gasPaymentParams={submitButtonState.expressParams?.gasPaymentParams}
            totalExecutionFee={submitButtonState.totalExecutionFee}
          />
        </div>
      </form>
    </>
  );
}
